import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { useGetActiveLocationsQuery } from '../../redux/employeeApi';
import { useGetOutletsQuery, useUpdateOutletStatusMutation } from '../../redux/outletApi';
import { useAuth } from '../../context/AuthContext';
import TablePagination from './TablePagination';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

function AdminOutletsList() {
  const { canAction } = useAuth();
  const canView = canAction('outlets', 'view');
  const canCreate = canAction('outlets', 'create');
  const canEdit = canAction('outlets', 'edit');
  const canDelete = canAction('outlets', 'delete');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const pageSize = DEFAULT_PAGE_SIZE;
  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    return params;
  }, [page, pageSize, search]);
  const { data: outletsRes, isFetching, isLoading, error, refetch } = useGetOutletsQuery(listQuery);
  const { data: locationsRes } = useGetActiveLocationsQuery();
  const [updateOutletStatus, { isLoading: isUpdatingStatus }] =
    useUpdateOutletStatusMutation();
  const [viewOutlet, setViewOutlet] = useState(null);
  const rows = useMemo(() => {
    const list = outletsRes?.data || outletsRes || [];
    const safe = Array.isArray(list) ? list : [];
    return safe
      .slice()
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map((o) => {
        const locationName =
          o.location_name ||
          o.locationName ||
          o.location?.name ||
          o.location_detail?.name ||
          o.locationDetail?.name ||
          (locationsRes?.data || []).find((l) => Number(l.id) === Number(o.location_id))?.name ||
          o.location ||
          '';
        const active = o.status === true || o.status === 'true' || o.status === 1;
        return {
          id: o.id,
          location: locationName,
          address: o.address || '',
          email: o.email || '',
          contact: o.contact || '',
          fssai: o.fssai_number || o.fssai || '',
          gst: o.gst_number || o.gst || '',
          google_url: o.google_url || '',
          swiggy_url: o.swiggy_url || '',
          zomato_url: o.zomato_url || '',
          fssai_image_url:
            resolveAssetUrl(o.fssai_image) ||
            '',
          gst_image_url:
            resolveAssetUrl(o.gst_image_url) ||
            resolveAssetUrl(o.gstImageUrl) ||
            resolveAssetUrl(o.gst_image) ||
            '',
          active,
          _rawStatus: o.status,
        };
      });
  }, [outletsRes, locationsRes]);

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    outletsRes,
    { page, pageSize }
  );
  const pageItems = rows;

  const toggleActive = (outlet) => {
    if (isUpdatingStatus) return;
    updateOutletStatus({ id: Number(outlet.id), status: !outlet.active })
      .unwrap()
      .then(() => toast.success('Outlet status updated successfully.'))
      .catch((err) =>
        toast.error(err?.data?.message || 'Failed to update outlet status.')
      );
  };

  return (
    <DashboardLayout title="Outlets List">
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full min-w-0 md:max-w-xs">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search Outlets"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            {canCreate ? (
              <Link
                to="/dashboard/admin/outlets/add"
                className="inline-flex w-full shrink-0 items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm md:w-auto"
              >
                + Add Outlet
              </Link>
            ) : null}
          </div>

          <ApiLoadState
            isLoading={isLoading}
            isFetching={isFetching}
            hasData={rows.length > 0}
            error={error}
            refetch={refetch}
            message="Loading outlets…"
            className="py-6 md:hidden"
          />
          {!error && (rows.length > 0 || (!isLoading && !isFetching)) && pageItems.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500 md:hidden">No outlets found.</p>
          ) : !error && (rows.length > 0 || (!isLoading && !isFetching)) && pageItems.length > 0 ? (
            <ul className="mb-4 space-y-3 md:hidden">
              {pageItems.map((o, idx) => (
                <li
                  key={o.id}
                  className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500">
                        #{(currentPage - 1) * pageSize + idx + 1}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900">{o.location}</p>
                      <p className="mt-2 text-xs leading-relaxed text-gray-600">{o.address || '—'}</p>
                      <p className="mt-2 break-all text-xs text-gray-600">
                        <span className="font-medium text-gray-800">{o.email || '—'}</span>
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        Contact:{' '}
                        <span className="font-medium text-gray-800">{o.contact || '—'}</span>
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {canView ? (
                        <button
                          type="button"
                          onClick={() => setViewOutlet(o)}
                          className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                          aria-label="View outlet"
                        >
                          <FaEye className="h-5 w-5" />
                        </button>
                      ) : null}
                      {canEdit ? (
                        <Link
                          to={`/dashboard/admin/outlets/edit/${o.id}`}
                          className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                          aria-label="Edit outlet"
                        >
                          <MdEdit className="h-5 w-5" />
                        </Link>
                      ) : null}
                    </div>
                  </div>
                  {canDelete ? (
                    <div className="mt-3 flex items-center justify-between border-t border-gray-200/80 pt-3">
                      <span className="text-xs text-gray-500">
                        {o.active ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        type="button"
                        disabled={isUpdatingStatus}
                        onClick={() => toggleActive(o)}
                        aria-label={o.active ? 'Disable outlet' : 'Enable outlet'}
                        className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:opacity-50 ${
                          o.active
                            ? 'border-emerald-600 bg-[#008E36]'
                            : 'border-gray-300 bg-gray-300'
                        }`}
                      >
                        <span
                          className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            o.active ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="">
                  <th className="whitespace-nowrap border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Sr. No.
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Location
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Address
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Email
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Contact Detail
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <ApiLoadTableRow
                  colSpan={6}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  hasData={rows.length > 0}
                  error={error}
                  refetch={refetch}
                  message="Loading outlets..."
                />
                {!error && (rows.length > 0 || (!isLoading && !isFetching)) &&
                  pageItems.map((o, idx) => (
                  <tr
                    key={o.id}
                    className={`hover:bg-amber-50/40 
                    }`}
                  >
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2.5 text-gray-600">
                      {(currentPage - 1) * pageSize + idx + 1}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                      {o.location}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                      {o.address}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                      {o.email}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5 text-gray-700">
                      {o.contact}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5">
                      <div className="flex items-center gap-3">
                        {canView ? (
                          <button
                            type="button"
                            onClick={() => setViewOutlet(o)}
                            className="text-red-700 hover:text-red-800"
                            aria-label="View outlet"
                          >
                            <FaEye className="h-5 w-5 text-red-700" />
                          </button>
                        ) : null}
                        {canEdit ? (
                          <Link
                            to={`/dashboard/admin/outlets/edit/${o.id}`}
                            className="text-red-700 hover:text-red-800"
                            aria-label="Edit outlet"
                          >
                            <MdEdit className="h-5 w-5 text-red-700" />
                          </Link>
                        ) : null}
                        {canDelete ? (
                        <button
                          type="button"
                          disabled={isUpdatingStatus}
                          onClick={() => toggleActive(o)}
                          aria-label={o.active ? 'Disable outlet' : 'Enable outlet'}
                          className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:opacity-50 ${
                            o.active
                              ? 'border-emerald-600 bg-[#008E36]'
                              : 'border-gray-300 bg-gray-300'
                          }`}
                        >
                          <span
                            className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                              o.active ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {!error && !isLoading && !isFetching && pageItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-500">
                      No outlets found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <TablePagination
            totalCount={totalCount}
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={effectivePageSize}
          />
        </div>
      </div>

      {viewOutlet && (
        <div
          className="fixed inset-0 z-40 flex justify-end bg-black/30"
          onClick={() => setViewOutlet(null)}
          role="presentation"
        >
          <div
            className="flex h-full w-full min-w-0 max-w-full flex-col bg-white shadow-2xl sm:max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
              <h2 className="text-lg font-semibold text-brand">Outlet Information</h2>
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                onClick={() => setViewOutlet(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-10 sm:px-6 sm:pb-8">
              <div className="grid grid-cols-2 gap-3">
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-medium text-gray-500">FSSAI Image</p>
                  <div className="aspect-[4/3] overflow-hidden rounded-lg bg-gray-100">
                    {viewOutlet.fssai_image_url ? (
                      <img
                        src={viewOutlet.fssai_image_url}
                        alt="FSSAI"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-gray-400">
                        Not uploaded
                      </div>
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-medium text-gray-500">GST Image</p>
                  <div className="aspect-[4/3] overflow-hidden rounded-lg bg-gray-100">
                    {viewOutlet.gst_image_url ? (
                      <img
                        src={viewOutlet.gst_image_url}
                        alt="GST"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-gray-400">
                        Not uploaded
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-2 sm:grid sm:grid-cols-2 sm:gap-3 sm:space-y-0">
                {[
                  { label: 'Location', value: viewOutlet.location },
                  { label: 'Email Id', value: viewOutlet.email, wide: true },
                  { label: 'Contact Detail', value: viewOutlet.contact },
                  { label: 'FSSAI Number', value: viewOutlet.fssai || '-' },
                  { label: 'GST Number', value: viewOutlet.gst || '-' },
                  { label: 'Address', value: viewOutlet.address, wide: true },
                ].map((field) => (
                  <div
                    key={field.label}
                    className={`rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 ${
                      field.wide ? 'sm:col-span-2' : ''
                    }`}
                  >
                    <p className="text-xs text-gray-500">{field.label}</p>
                    <p className={`mt-0.5 text-sm font-medium text-gray-900 ${field.wide ? 'break-words' : ''}`}>
                      {field.value || '-'}
                    </p>
                  </div>
                ))}
                {[
                  { label: 'Google URL', href: viewOutlet.google_url },
                  { label: 'Swiggy URL', href: viewOutlet.swiggy_url },
                  { label: 'Zomato URL', href: viewOutlet.zomato_url },
                ].map((link) => (
                  <div
                    key={link.label}
                    className="rounded-lg border border-gray-100 bg-[#faf8f5] px-3 py-2.5 sm:col-span-2 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0"
                  >
                    <p className="text-xs text-gray-500">{link.label}</p>
                    {link.href ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-0.5 block break-all text-sm font-medium text-brand hover:underline"
                      >
                        {link.href}
                      </a>
                    ) : (
                      <p className="mt-0.5 text-sm font-medium text-gray-900">-</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default AdminOutletsList;

